/**
 * Admin · Activation & licensing (009 ops-api). Leads with a clickable LICENSES
 * browser: click a license to see everything tied to it (validity, seats, assigned
 * customers, every device) and control it (LicenseDetailPanel). Below that, the
 * current session's activation state + a per-customer device lookup remain.
 * Server enforces (006/009).
 */
import { useEffect, useState } from "react";
import { EmptyState } from "../ui/EmptyState.js";
import { useServices } from "../app/context.js";
import { InfoHint } from "../ui/Tooltip.js";
import type { ActivationStatus, BoundDevice, DeviceInfo, LicenseSummary } from "@genz/platform";
import { useSessionStore } from "../stores/session.js";
import { Drawer } from "../ui/Drawer.js";
import { LicenseDetailPanel } from "./LicenseDetailPanel.js";
import { DeviceDetailPanel } from "./DeviceDetailPanel.js";
import { GenerateLicenseForm, StatusBadge, fmtDate, fmtRelative } from "./detail-shared.js";
import { CopyButton } from "../ui/CopyButton.js";
import { enumLabel } from "../format/labels.js";
import { dedupeDevicesByFingerprint } from "./dedupeDevices.js";
import { useCachedResource } from "../stores/useCachedResource.js";
import { StaleNotice } from "./StaleNotice.js";
import { cacheEpoch, readCacheEntry, writeCache } from "../stores/cache.js";
import type { Staleness } from "../stores/useCachedResource.js";
import { RowAction } from "../ui/RowAction.js";

/** Cache key for the session's activation status — seeds instantly on tab re-open. */
const ACTIVATION_STATUS_CACHE = "admin.activationStatus";

export function ActivationTab() {
  const { client } = useServices();
  const isSuper = useSessionStore((s) => s.isSuperAdmin());
  const cachedStatus = readCacheEntry<ActivationStatus>(ACTIVATION_STATUS_CACHE);
  const [status, setStatus] = useState<ActivationStatus | null>(cachedStatus?.data ?? null);
  const [loading, setLoading] = useState(cachedStatus == null);
  const [error, setError] = useState<string | null>(null);
  // GEN-8: same swallowed-refresh bug as the other cache-first surfaces.
  const [statusStale, setStatusStale] = useState<Staleness | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  // Licenses browser (click → full detail drawer). Cache-first across tab switches.
  const { data: licensesData, error: licensesError, stale: licensesStale, revalidate: refreshLicenses } =
    useCachedResource<LicenseSummary[]>("admin.licenses", () => client.listLicenses());
  const licenses = licensesData ?? [];
  const [licenseFor, setLicenseFor] = useState<LicenseSummary | null>(null);

  // Bound devices browser (org-wide; Manage → device detail drawer). Cache-first.
  const { data: boundData, error: boundError, stale: boundStale, revalidate: refreshBoundDevices } =
    useCachedResource<BoundDevice[]>("admin.boundDevices", () => client.listDevices());
  // One row per physical device. Org-wide list → key by customer+fingerprint so the SAME
  // device under two different customers isn't collapsed into one.
  const boundDevices = dedupeDevicesByFingerprint(boundData ?? [], (d) => `${d.customer_id ?? ""}::${d.fingerprint}`);
  const [boundDeviceFor, setBoundDeviceFor] = useState<BoundDevice | null>(null);

  // Per-customer device lookup (optional drill-down).
  const [customerId, setCustomerId] = useState("");
  const [customerDevices, setCustomerDevices] = useState<DeviceInfo[] | null>(null);

  async function refresh() {
    const cached = readCacheEntry<ActivationStatus>(ACTIVATION_STATUS_CACHE);
    if (!cached) setLoading(true);  // only spin when there's nothing cached to show
    setError(null);
    const epoch = cacheEpoch();
    try {
      const s = await client.activationStatus();
      writeCache(ACTIVATION_STATUS_CACHE, s, epoch);
      setStatus(s);
      setStatusStale(null);
    } catch (e) {
      const reason = e instanceof Error ? e.message : String(e);
      if (!cached) setError(reason);
      else setStatusStale({ cachedAt: cached.at, reason });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  async function deactivate(deviceId: string) {
    setBusy(deviceId);
    setError(null);
    try {
      await client.deactivateDevice(deviceId);
      await refresh();
      if (customerDevices) await lookupCustomer();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  async function lookupCustomer(e?: React.FormEvent) {
    e?.preventDefault();
    if (!customerId) return;
    setError(null);
    try {
      setCustomerDevices(await client.listCustomerDevices(customerId));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setCustomerDevices([]);
    }
  }

  // These lists may not carry a fingerprint at runtime → key by fingerprint when present,
  // else the always-unique device_id (so they're never wrongly collapsed into one row).
  const devices = dedupeDevicesByFingerprint(status?.devices ?? [], (d) => d.fingerprint || d.device_id);
  const dedupedCustomerDevices = customerDevices
    ? dedupeDevicesByFingerprint(customerDevices, (d) => d.fingerprint || d.device_id)
    : null;
  const statusBadge =
    status?.status === "active"
      ? "badge--success"
      : status?.status === "grace"
        ? "badge--warn"
        : "badge--danger";

  return (
    <section className="admin-tab" data-testid="admin-activation">
      <div className="admin-panel__head admin-panel__head--flush">
        <div>
          <strong className="admin-panel__title panel-title">Activation &amp; licensing <InfoHint text="Click a license to see its validity, seats, assigned customers and devices — and control all of it." /></strong>
        </div>
      </div>

      {/* Generate a new license (super_admin only — server enforces too). */}
      {isSuper && (
        <GenerateLicenseForm
          onCreated={() => void refreshLicenses()}
        />
      )}

      {/* Licenses browser */}
      <div>
        <h3 className="admin-section-title">Licenses</h3>
        <p className="admin-section-title__sub">Click a license to see validity, seats, assigned customers and devices.</p>
      </div>
      {licensesError && <p role="alert">{licensesError}</p>}
      <StaleNotice stale={licensesStale} testid="licenses-stale" />
      {licenses.length === 0 ? (
        <EmptyState
          icon="settings"
          title="No licenses"
          hint="Issued licenses for your organization will appear here."
          testId="licenses-empty"
        />
      ) : (
        <div className="table-scroll"><table className="table" data-testid="licenses-table">
          <thead>
            <tr><th>Key</th><th>Tier</th><th className="num">Seats</th><th className="num">Devices</th><th>Validity</th><th>Status</th><th /></tr>
          </thead>
          <tbody>
            {licenses.map((l) => (
              <tr key={l.id} data-testid="license-row">
                <td>
                  <span className="id-cell">
                    <button
                      type="button"
                      className="link-btn"
                      onClick={() => setLicenseFor(l)}
                      data-testid="license-open"
                    >
                      <code>{l.license_key_masked}</code>
                    </button>
                    <CopyButton value={l.license_key_masked} label="Copy license key" />
                  </span>
                </td>
                <td><span className="badge badge--info">{enumLabel(l.tier)}</span></td>
                <td className="num">{l.seats_used} / {l.seats}</td>
                <td className="num">{l.devices_active}</td>
                <td>{l.expires_at ? fmtDate(l.expires_at) : "Perpetual"}</td>
                <td>
                  <span className={`badge ${l.expired ? "badge--danger" : "badge--success"}`}>
                    {l.expired ? "expired" : "valid"}
                  </span>
                </td>
                <td>
                  <RowAction label="Manage" hint="Manage this license" icon="arrow-right" tone="quiet"
                    onClick={() => setLicenseFor(l)} data-testid="license-manage" />
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}

      {/* Bound devices browser (org-wide). Click Manage → device detail drawer. */}
      <h3 className="admin-section-title">Bound devices</h3>
      {boundError && <p role="alert">{boundError}</p>}
      <StaleNotice stale={boundStale} testid="bound-devices-stale" />
      {boundDevices.length === 0 ? (
        <EmptyState
          icon="settings"
          title="No bound devices"
          hint="Activated desktops across your licenses will appear here."
          testId="bound-devices-empty"
        />
      ) : (
        <div className="table-scroll"><table className="table" data-testid="bound-devices-table">
          <thead>
            <tr><th>Device</th><th>Fingerprint</th><th>Customer</th><th>Last seen</th><th>Status</th><th /></tr>
          </thead>
          <tbody>
            {boundDevices.map((d) => (
              <tr key={d.device_id} data-testid="bound-device-row">
                <td><strong>{d.label ?? d.device_id}</strong></td>
                <td>
                  <span className="id-cell">
                    <code>{d.fingerprint}</code>
                    <CopyButton value={d.fingerprint} label="Copy fingerprint" />
                  </span>
                </td>
                <td>{d.customer_email ?? d.customer_id ?? "—"}</td>
                <td>{fmtRelative(d.last_seen_at)}</td>
                <td><StatusBadge status={d.active ? "active" : "grace_lapsed"} /></td>
                <td>
                  <RowAction label="Manage" hint="Manage this device" icon="arrow-right" tone="quiet"
                    onClick={() => setBoundDeviceFor(d)} data-testid="bound-device-manage" />
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}

      {/* Current session activation */}
      <h3 className="admin-section-title">This session</h3>
      {error && <p role="alert">{error}</p>}
      <StaleNotice stale={statusStale} testid="activation-stale" />
      {loading ? (
        <p className="faint" data-testid="activation-loading">Loading activation status…</p>
      ) : (
        <>
          <div className="admin-tab__stats" data-testid="activation-summary">
            <span className={`badge ${statusBadge}`} data-testid="activation-status">{status?.status ?? "unknown"}</span>
            <span className="faint">{devices.length} device(s) active</span>
            {status?.grace_until && <span className="faint">grace until {status.grace_until}</span>}
          </div>

          {devices.length === 0 ? (
            <EmptyState
              icon="settings"
              title="No active devices"
              hint="Activated desktops will appear here."
              testId="activation-empty"
            />
          ) : (
            <div className="table-scroll"><table className="table" data-testid="activation-table">
              <thead>
                <tr><th>Device</th><th>Fingerprint</th><th>Last seen</th><th /></tr>
              </thead>
              <tbody>
                {devices.map((d) => (
                  <tr key={d.device_id} data-testid="activation-device">
                    <td>{d.label ?? d.device_id}</td>
                    <td>
                      <span className="id-cell">
                        <code>{d.fingerprint}</code>
                        <CopyButton value={d.fingerprint} label="Copy fingerprint" />
                      </span>
                    </td>
                    <td>{d.last_seen_at ? fmtRelative(d.last_seen_at) : (d.last_seen ?? <span className="faint">—</span>)}</td>
                    <td>
                      <RowAction
                        label="Deactivate"
                        icon="power"
                        tone="danger"
                        onClick={() => deactivate(d.device_id)}
                        disabled={busy === d.device_id}
                        busy={busy === d.device_id}
                        data-testid="activation-deactivate"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          )}
        </>
      )}

      <form className="admin-tab__form" onSubmit={lookupCustomer} data-testid="activation-lookup-form">
        <div className="field">
          <label htmlFor="act-customer">Customer id</label>
          <input
            id="act-customer"
            type="text"
            value={customerId}
            onChange={(e) => setCustomerId(e.target.value)}
            placeholder="customer id"
            data-testid="activation-customer-id"
          />
        </div>
        <button type="submit" className="btn" disabled={!customerId} data-testid="activation-lookup">
          View devices
        </button>
      </form>

      {dedupedCustomerDevices && (
        <div className="admin-tab__drawer" data-testid="activation-customer-devices">
          <h3 className="admin-section-title">Customer devices</h3>
          {dedupedCustomerDevices.length === 0 ? (
            <p className="faint">No active devices for this customer.</p>
          ) : (
            <ul>
              {dedupedCustomerDevices.map((d) => (
                <li key={d.device_id} data-testid="activation-customer-device">
                  {d.label ?? d.device_id} · {d.fingerprint}
                  {/* WS-Z9 / F-2: the same Deactivate the tables above carry, and until now the
                      only one with NO testid and NO accessible name of its own. */}
                  <RowAction
                    label="Deactivate"
                    icon="power"
                    tone="danger"
                    className="badge--trailing"
                    onClick={() => deactivate(d.device_id)}
                    disabled={busy === d.device_id}
                    busy={busy === d.device_id}
                    data-testid="activation-customer-device-deactivate"
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {licenseFor && (
        <Drawer
          title={<code>{licenseFor.license_key_masked}</code>}
          subtitle="License key"
          badge={<StatusBadge status={licenseFor.expired ? "expired" : "active"} />}
          onClose={() => setLicenseFor(null)}
          testid="license-detail-drawer"
        >
          <LicenseDetailPanel
            licenseId={licenseFor.id}
            onChanged={() => {
              void refreshLicenses();
              void refreshBoundDevices();
            }}
            onDeleted={() => {
              setLicenseFor(null);
              void refreshLicenses();
              void refreshBoundDevices();
            }}
          />
        </Drawer>
      )}

      {boundDeviceFor && (
        <Drawer
          title={boundDeviceFor.label ?? boundDeviceFor.device_id}
          subtitle="Device"
          badge={<StatusBadge status={boundDeviceFor.active ? "active" : "grace_lapsed"} />}
          onClose={() => setBoundDeviceFor(null)}
          testid="device-detail-drawer"
        >
          <DeviceDetailPanel
            device={boundDeviceFor}
            customerLabel={boundDeviceFor.customer_email}
            onChanged={() => void refreshBoundDevices()}
            onClose={() => setBoundDeviceFor(null)}
          />
        </Drawer>
      )}
    </section>
  );
}
